import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import TiltCard from "@/components/TiltCard";
import Reveal from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const IMG_WOLF = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/470tjtja_3c27f666-b61d-4192-818e-84fd7bd620d9.png";
const IMG_TOBIAS = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/o4mpbuse_ChatGPT%20Image%2024.%20Juni%202026%2C%2021_34_57.png";
const IMG_MORITZ = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/7zomflun_ChatGPT%20Image%2024.%20Juni%202026%2C%2021_24_48.png";
const IMG_KRANTZ = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts/es45ryc6_ChatGPT%20Image%2024.%20Juni%202026%2C%2021_29_41%20Kopie.png";
const IMG_KRANTZ_TILE = "/krantz.png";

const CONTACTS = {
  wolf: { price: "90€", phone: "0151-41929686", email: "training@wolfmichael.de" },
  tobias: { price: "85€", phone: "0151-53984163", email: "Golfpro0711@gmail.com" },
  krantz: { price: "85€", phone: "0172 9193016" },
  moritz: { price: "75€", phone: "0176 71234706" },
};

const TrainerBlock = ({ data, img, label, reverse, testid, contact, lang }) => {
  const calLabel = lang === "de" ? "ZUM KALENDER" : "TO CALENDAR";
  const priceLabel = lang === "de" ? "Preis" : "Price";
  const extraLabel = lang === "de" ? "(jede weitere Person +5€)" : "(each additional person +5€)";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-testid={testid}>
      <div className={`lg:col-span-5 fade-up ${reverse ? "lg:order-2" : "lg:order-1"}`}>
        <div className="relative border border-white/10 bg-black">
          {img ? (
            <img src={img} alt={data.title} className="w-full h-[460px] md:h-[600px] object-cover object-top" />
          ) : (
            <div className="w-full h-[460px] md:h-[600px] flex items-center justify-center bg-gradient-to-b from-[#161616] to-[#050505]">
              <span className="font-heading text-8xl md:text-9xl text-white/10 uppercase tracking-tight">{data.monogram}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <span className="font-heading text-3xl md:text-5xl tracking-tight text-[#ff0033] uppercase leading-none">{label}</span>
          </div>
        </div>
      </div>

      <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
        <span className="font-mono-lab text-xs md:text-sm tracking-[0.25em] text-[#ff0033] uppercase">{data.kicker}</span>
        <h2 className="font-heading text-5xl md:text-7xl uppercase tracking-tight leading-none text-white mt-4">
          {data.title}
        </h2>
        <p className="font-mono-lab text-xs md:text-sm tracking-[0.15em] text-neutral-400 uppercase mt-4">
          {data.role}
        </p>
        <div className="mt-8 space-y-5">
          {data.bio.map((p, i) => (
            <p key={`bio-${i}`} className="text-neutral-300 text-base md:text-lg leading-relaxed">{p}</p>
          ))}
        </div>

        <div className="mt-10 border-t border-white/10 pt-8">
          <p className="font-mono-lab text-xs tracking-[0.2em] text-neutral-500 uppercase mb-5">{data.qualsTitle}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.quals.map((q) => (
              <li key={q} className="flex items-center gap-3 text-neutral-200">
                <Check size={16} className="text-[#ff0033] shrink-0" />
                <span className="text-sm">{q}</span>
              </li>
            ))}
          </ul>
        </div>

        <Link
          to="/einzeltraining"
          data-testid={`${testid}-cta`}
          className="inline-flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors mt-10"
        >
          {calLabel} <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
};
const _UNUSED_TRAINERBLOCK = TrainerBlock; void _UNUSED_TRAINERBLOCK;


const Divider = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="border-t border-white/10" />
  </div>
);

const TrainerTiles = ({ tiles, lang }) => {
  const profileLabel = lang === "de" ? "Zum Profil" : "View profile";
  const bookLabel = lang === "de" ? "Zur Buchung" : "Book now";
  const [active, setActive] = useState(null);
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto" data-testid="trainer-tiles">
        {tiles.map((tile, idx) => (
          <Reveal key={tile.id} direction={idx % 2 === 0 ? "left" : "right"} delay={(idx % 2) * 0.1} className="h-full">
          <TiltCard
            max={5}
            data-testid={`trainer-tile-${tile.key}`}
            className="bg-[#0F0F0F] border border-white/10 hover:border-[#ff0033]/50 transition-colors flex flex-col overflow-hidden h-full"
          >
            <div className="relative aspect-[4/5] bg-black overflow-hidden">
              {tile.img ? (
                <img
                  src={tile.img}
                  alt={tile.name}
                  loading="lazy"
                  className={`w-full h-full object-cover ${tile.imgPos || "object-top"}`}
                  data-testid={`trainer-tile-img-${tile.key}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-[#161616] to-[#050505]">
                  <span className="font-heading text-6xl text-white/10 uppercase">{tile.monogram}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-heading text-xl md:text-2xl uppercase tracking-tight text-white leading-none">{tile.name}</h3>
              <p className="font-mono-lab text-[10px] md:text-xs tracking-[0.15em] text-[#ff0033] uppercase mt-2" style={{ textShadow: "none" }}>
                {tile.role}
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <button
                  onClick={() => setActive(tile)}
                  data-testid={`tile-profile-${tile.key}`}
                  className="w-full border border-white/20 text-white font-heading text-sm md:text-base uppercase tracking-widest py-3.5 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors"
                >
                  {profileLabel}
                </button>
                <Link
                  to="/einzeltraining"
                  data-testid={`tile-book-${tile.key}`}
                  className="w-full bg-[#ff0033] text-white font-heading text-sm md:text-base uppercase tracking-widest py-3.5 text-center hover:bg-[#cc0029] transition-colors"
                >
                  {bookLabel}
                </Link>
              </div>
            </div>
          </TiltCard>
          </Reveal>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-[#0B0B0B] border-white/10 text-white max-h-[88vh] overflow-y-auto" data-testid="trainer-modal">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.name}</DialogTitle>
              <div className="flex flex-col sm:flex-row gap-5">
                <img src={active.img} alt={active.name} className="w-full sm:w-40 h-52 object-cover object-top border border-white/10 shrink-0" />
                <div>
                  <span className="font-mono-lab text-xs tracking-[0.25em] text-[#ff0033] uppercase">{active.data.kicker}</span>
                  <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white mt-2 leading-none">{active.name}</h2>
                  <p className="font-mono-lab text-xs tracking-[0.15em] text-neutral-400 uppercase mt-2">{active.role}</p>
                  <div className="mt-4 text-neutral-300 text-sm space-y-1">
                    <a href={`tel:${active.contact.phone}`} className="flex items-center gap-2 hover:text-[#ff0033] transition-colors">
                      <Phone size={14} className="text-[#ff0033]" /> {active.contact.phone}
                    </a>
                    {active.contact.email && (
                      <a href={`mailto:${active.contact.email}`} className="flex items-center gap-2 hover:text-[#ff0033] transition-colors break-all">
                        <Mail size={14} className="text-[#ff0033]" /> {active.contact.email}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {active.data.bio.map((p, i) => (
                  <p key={`m-bio-${i}`} className="text-neutral-300 text-sm leading-relaxed">{p}</p>
                ))}
              </div>

              <div className="mt-6 border-t border-white/10 pt-5">
                <p className="font-mono-lab text-xs tracking-[0.2em] text-neutral-500 uppercase mb-3">{active.data.qualsTitle}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {active.data.quals.map((q) => (
                    <li key={q} className="flex items-center gap-2 text-neutral-200">
                      <Check size={14} className="text-[#ff0033] shrink-0" />
                      <span className="text-sm">{q}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/einzeltraining"
                onClick={() => setActive(null)}
                data-testid="trainer-modal-cta"
                className="inline-flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-3 mt-6 hover:bg-[#cc0029] transition-colors"
              >
                {bookLabel} <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Trainer = () => {
  const { t, lang } = useLang();
  const tiles = [
    { key: "wolf", id: "profile-wolf", name: t.trainer.title, role: "PGA Head Professional", img: IMG_WOLF, data: t.trainer, contact: CONTACTS.wolf },
    { key: "tobias", id: "profile-tobias", name: t.trainer2.title, role: "PGA Professional", img: IMG_TOBIAS, data: t.trainer2, contact: CONTACTS.tobias },
    { key: "krantz", id: "profile-krantz", name: t.trainer4.title, role: "PGA Professional", img: IMG_KRANTZ_TILE, imgPos: "object-bottom", data: t.trainer4, contact: CONTACTS.krantz },
    { key: "moritz", id: "profile-moritz", name: t.trainer3.title, role: "PGA Professional", img: IMG_MORITZ, data: t.trainer3, contact: CONTACTS.moritz },
  ];
  return (
    <div data-testid="trainer-page">
      <section className="pt-32 md:pt-40 pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="font-mono-lab text-xs md:text-sm tracking-[0.25em] text-[#ff0033] uppercase">// {lang === "de" ? "UNSER TEAM" : "OUR TEAM"}</span>
        <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl uppercase tracking-tight leading-none text-white mt-3 mb-4 fade-up">
          {lang === "de" ? "Trainer" : "Coaches"}
        </h1>
        <p data-testid="trainer-slogan" className="font-heading text-xl sm:text-2xl md:text-3xl uppercase tracking-tight text-[#ff0033] mb-10 fade-up" style={{ textShadow: "0 0 18px rgba(255,0,51,0.5)" }}>
          {lang === "de" ? "Ein Team. Ein Ziel. Dein Erfolg." : "One Team. One Goal. Your Success."}
        </p>
        <TrainerTiles tiles={tiles} lang={lang} />

        {/* Weitere-Trainer */}
        <div className="mt-20 md:mt-28" data-testid="weitere-trainer-section">
          <span className="font-mono-lab text-xs md:text-sm tracking-[0.25em] text-[#ff0033] uppercase block text-center">// {lang === "de" ? "Breitensport" : "Group Coaching"}</span>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight leading-none text-white mt-3 mb-10 text-center fade-up">
            Weitere-Trainer
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            {[
              { key: "nico", name: "Nico Mihaylov", img: "/nico-mihaylov.jpg" },
              { key: "lara", name: "Lara Brinks", img: "/lara-brinks.jpg" },
            ].map((p, idx) => (
              <Reveal key={p.key} direction={idx % 2 === 0 ? "left" : "right"} delay={(idx % 2) * 0.1} className="h-full">
                <div
                  data-testid={`weitere-trainer-${p.key}`}
                  className="bg-black border border-white/10 hover:border-[#ff0033]/50 transition-colors flex flex-col overflow-hidden h-full"
                >
                  <div className="relative aspect-[4/5] bg-black overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                      data-testid={`weitere-trainer-img-${p.key}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" />
                  </div>
                  <div className="p-5 bg-black">
                    <h3 className="font-heading text-xl md:text-2xl uppercase tracking-tight text-white leading-none">{p.name}</h3>
                    <p className="font-mono-lab text-[10px] md:text-xs tracking-[0.15em] text-[#ff0033] uppercase mt-2" style={{ textShadow: "none" }}>
                      C-Trainer Breitensport
                    </p>
                    <p className="text-neutral-400 text-sm mt-3">
                      {lang === "de" ? "Als Gruppentrainer in der GCA aktiv" : "Active as a group coach at the GCA"}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Trainer;
